import { Checkbox } from 'baseui/checkbox'
import classNames from 'classnames'
import React, { Ref, useRef } from 'react'
import { CustomButton } from '../../components/Button'
import { LiveViewStream } from './components/LiveViewStream'
import { useBlackmaskOperations } from './hooks'
import styles from './Blackmask.module.css'

export interface BlackmaskProps {
    fullscreen?: boolean
    controlWidth?: string
}

export interface Point {
    x: number
    y: number
}

export interface NormalizedPoint {
    x: number // 0-1 value
    y: number // 0-1 value
}

export interface Blackmask {
    id: number
    points: NormalizedPoint[]
}

export const COLORS = [
    '#FF0000',
    '#00FF00',
    '#0000FF',
    '#FFFF00',
    '#FF00FF',
    '#00FFFF',
    '#800000',
    '#008000',
    '#000080',
    '#808000',
    '#800080',
    '#008080',
    '#FFA500',
    '#FFC0CB',
    '#7FFFD4',
    '#8A2BE2',
    '#A52A2A',
    '#DEB887',
    '#5F9EA0',
    '#7FFF00'
]

const Blackmask: React.FC<BlackmaskProps> = ({ fullscreen = false }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const imageRef = useRef<HTMLIFrameElement>(null)

    const {
        blackmasks,
        editMode,
        stickToEdges,
        colorIndices,
        IS_BLACK_MASK_SUPPORTED,
        IS_POLYGON_SUPPORTED,
        maxPoints,
        MAX_OVERLAYS,
        updateCanvasWrapper,
        handleCanvasClick,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        handleEdit,
        handleDelete,
        handleAddShape,
        setStickToEdges,
        blackmaskEnabled,
        handleBlackmaskEnabledChangeAction
    } = useBlackmaskOperations(canvasRef, imageRef)
    if (!IS_BLACK_MASK_SUPPORTED) {
        return <>Black mask is not supported</>
    }
    if (!IS_POLYGON_SUPPORTED) {
        return <>Polygon is not supported</>
    }

    return (
        <div className={classNames(styles.root, { [styles.fullscreen]: fullscreen })}>
            <div className={styles.streamWrapper}>
                <LiveViewStream
                    updateParent={(ref) => {
                        updateCanvasWrapper(ref)
                    }}
                    ref={imageRef as unknown as Ref<HTMLImageElement>}
                    fullscreen={fullscreen}
                />
                <canvas
                    ref={canvasRef}
                    onClick={handleCanvasClick}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    className={styles.canvas}
                />
            </div>
            <div className={classNames(styles.controls, { [styles.fullscreen]: fullscreen })}>
                {blackmasks.map((mask, index) => (
                    <div key={index} className={styles.maskRow}>
                        <div className={styles.maskLabel}>
                            <span className={classNames(styles.maskName, { [styles.fullscreen]: fullscreen })}>
                                Overlay {index + 1}
                            </span>
                            <div
                                className={classNames(styles.colorIndicator, { [styles.fullscreen]: fullscreen })}
                                style={{ backgroundColor: COLORS[colorIndices[index]] }}
                            />
                        </div>
                        <div>
                            <CustomButton
                                extraProps={{ width: '30px', minWidth: '30px' }}
                                onClick={() => handleEdit(index)}
                                disabled={editMode === index}
                            >
                                Edit
                            </CustomButton>
                            <CustomButton
                                onClick={() => handleDelete(index)}
                                disabled={mask.points.length !== maxPoints}
                                extraProps={{
                                    marginLeft: '10px',
                                    width: '30px',
                                    minWidth: '30px'
                                }}
                            >
                                Delete
                            </CustomButton>
                        </div>
                    </div>
                ))}
                <div className={styles.footer}>
                    <div className={styles.checkboxGroup}>
                        <Checkbox
                            checked={stickToEdges}
                            onChange={(e) => setStickToEdges(e.target.checked)}
                            labelPlacement="right"
                        >
                            Stick to edges
                        </Checkbox>
                        <Checkbox
                            checked={blackmaskEnabled}
                            onChange={(e) => handleBlackmaskEnabledChangeAction(e.target.checked)}
                            labelPlacement="right"
                        >
                            Enable Blackmask
                        </Checkbox>
                    </div>
                    <CustomButton
                        onClick={handleAddShape}
                        disabled={
                            blackmasks.length >= MAX_OVERLAYS ||
                            (blackmasks.length > 0 && blackmasks[blackmasks.length - 1].points.length !== maxPoints)
                        }
                    >
                        Add Overlay
                    </CustomButton>
                </div>
            </div>
        </div>
    )
}

export default Blackmask
